import { Table, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { renderLoadingUntilDefined } from "../common/LoadingUntilDefined";
import { useSongbooksQuery } from "./useSongbooksQuery";
import { SongbookUb } from "../gql/graphql";
import { useState } from "react";
import { SongbookDialog } from "./SongbookDialog";
import { DeleteSongbookDialog } from "./DeleteSongbookDialog";
import { useTranslation } from "react-i18next";
import { CyManageSongbooks } from "./CyManageSongbooks";
import { ManagementPanel } from "../common/components/management/ManagementPanel";

interface Props {
  bucket: string;
}

export function SongbookManagement({ bucket }: Props) {
  const { t } = useTranslation();
  const { songbooks: songbooksMaybe, reexecuteQuery } = useSongbooksQuery();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingSongbook, setEditingSongbook] = useState<
    SongbookUb | undefined
  >(undefined);
  const [songbookToDelete, setSongbookToDelete] = useState<
    SongbookUb | undefined
  >(undefined);

  const openCreateDialog = () => {
    setEditingSongbook(undefined);
    setIsDialogOpen(true);
  };

  const openEditDialog = (songbook: SongbookUb) => {
    setEditingSongbook(songbook);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (songbook: SongbookUb) => {
    setSongbookToDelete(songbook);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <ManagementPanel
        title={bucket}
        actions={
          <Button color="primary" onClick={openCreateDialog}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {t("songbooks.create")}
          </Button>
        }
      >
        {renderLoadingUntilDefined(songbooksMaybe, (songbooks) => {
          const filteredSongbooks = songbooks
            .filter((s) => s.bucket === bucket)
            .sort((songbookA, songbookB) => {
              const mnemonicComparison = songbookA.mnemonic.localeCompare(
                songbookB.mnemonic,
              );
              if (mnemonicComparison !== 0) {
                return mnemonicComparison;
              }

              return songbookA.name.localeCompare(songbookB.name);
            });
          if (filteredSongbooks.length === 0) {
            return (
              <p className="text-muted management-empty">
                {t("songbooks.noSongbooksFound")}
              </p>
            );
          } else {
            return (
              <Table
                responsive
                hover
                className="management-table mb-0"
                data-cy={CyManageSongbooks.ManageSongbooksTable}
              >
                <thead>
                  <tr>
                    <th>{t("songbooks.table.mnemonic")}</th>
                    <th>{t("songbooks.table.name")}</th>
                    <th className="text-end">{t("songbooks.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSongbooks.map((s) => (
                    <tr key={s.id}>
                      <td className="align-middle">
                        <strong>{s.mnemonic}</strong>
                      </td>
                      <td className="align-middle">{s.name}</td>
                      <td className="text-end align-middle">
                        <div className="management-action-group">
                          <Button
                            color="primary"
                            size="sm"
                            data-cy={CyManageSongbooks.EditSongbookButton}
                            onClick={() => openEditDialog(s)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            data-cy={CyManageSongbooks.DeleteSongbookButton}
                            onClick={() => handleDeleteClick(s)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            );
          }
        })}
      </ManagementPanel>

      <SongbookDialog
        isOpen={isDialogOpen}
        toggle={() => setIsDialogOpen(!isDialogOpen)}
        bucket={bucket}
        songbook={editingSongbook}
        onSaved={reexecuteQuery}
      />

      <DeleteSongbookDialog
        isOpen={isDeleteDialogOpen}
        toggle={() => setIsDeleteDialogOpen(!isDeleteDialogOpen)}
        bucket={bucket}
        songbook={songbookToDelete}
        onDeleted={reexecuteQuery}
      />
    </>
  );
}
